import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Eye, CheckCircle, Clock, Bot, Loader2 } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

import { adminApi, BugReport, ApiError } from '../../lib/api';

const statusColors: Record<string, string> = {
  open: 'bg-rose-500/10 text-rose-600',
  in_progress: 'bg-amber-500/10 text-amber-600',
  resolved: 'bg-emerald-500/10 text-emerald-600',
  closed: 'bg-muted text-muted-foreground',
};

const priorityColors: Record<string, string> = {
  critical: 'bg-rose-500 text-white',
  high: 'bg-amber-500 text-white',
  medium: 'bg-blue-500/10 text-blue-600',
  low: 'bg-muted text-muted-foreground',
};

const BugsTab: React.FC = () => {
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [selectedBug, setSelectedBug] = useState<BugReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');

  const fetchBugs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.bugs({ status: filter || undefined, limit: 100 });
      setBugs(data.reports || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const viewBug = async (bugId: string) => {
    try {
      const data = await adminApi.getBug(bugId);
      setSelectedBug(data);
    } catch {
      // silently fail
    }
  };

  const updateBug = async (bugId: string, updates: Partial<BugReport>) => {
    try {
      await adminApi.resolveBug(bugId, updates);
      await fetchBugs();
      if (selectedBug?.id === bugId) {
        setSelectedBug({ ...selectedBug, ...updates });
      }
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  const openCount = bugs.filter((b) => b.status === 'open').length;
  const criticalCount = bugs.filter(
    (b) => b.priority === 'critical' && b.status !== 'closed'
  ).length;

  if (loading && bugs.length === 0) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs sm:text-sm">
          <p className="text-muted-foreground">{bugs.length} reports</p>
          {openCount > 0 && <Badge variant="destructive" className="text-[10px]">{openCount} open</Badge>}
          {criticalCount > 0 && <Badge className="bg-rose-500 text-[10px]">{criticalCount} critical</Badge>}
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 max-w-[200px] sm:max-w-none">
          {['', 'open', 'in_progress', 'resolved', 'closed'].map((s) => (
            <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm" className="text-[10px] h-7 capitalize px-2 shrink-0" onClick={() => setFilter(s)}>
              {s || 'All'}
            </Button>
          ))}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchBugs}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px]">Title</TableHead>
                  <TableHead className="text-[11px]">Priority</TableHead>
                  <TableHead className="text-[11px]">Status</TableHead>
                  <TableHead className="text-[11px] hidden sm:table-cell">Reporter</TableHead>
                  <TableHead className="text-[11px] hidden sm:table-cell text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bugs.map((bug) => (
                  <TableRow key={bug.id} className={`${selectedBug?.id === bug.id ? 'bg-muted/50' : ''} cursor-pointer`} onClick={() => viewBug(bug.id)}>
                    <TableCell className="font-medium text-xs max-w-[150px] truncate">{bug.title}</TableCell>
                    <TableCell><Badge className={`text-[9px] px-1 py-0 ${priorityColors[bug.priority]}`}>{bug.priority}</Badge></TableCell>
                    <TableCell><Badge className={`text-[9px] px-1 py-0 ${statusColors[bug.status]}`}>{bug.status}</Badge></TableCell>
                    <TableCell className="text-[10px] text-muted-foreground hidden sm:table-cell">{bug.reported_email?.split('@')[0] || '?'}</TableCell>
                    <TableCell className="text-right hidden sm:table-cell">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><Eye className="w-3 h-3" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>

        <div>
          {selectedBug ? (
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-sm">{selectedBug.title}</h3>
                <div className="flex gap-2">
                  <Badge className={`text-[10px] ${priorityColors[selectedBug.priority]}`}>{selectedBug.priority}</Badge>
                  <Badge className={`text-[10px] ${statusColors[selectedBug.status]}`}>{selectedBug.status}</Badge>
                </div>
                {selectedBug.description && <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{selectedBug.description}</p>}
                <div className="text-[10px] text-muted-foreground space-y-0.5 border-t pt-2">
                  <div>Page: <span className="text-foreground">{selectedBug.page}</span></div>
                  <div>Reporter: <span className="text-foreground">{selectedBug.reported_email}</span></div>
                  <div>Date: <span className="text-foreground">{new Date(selectedBug.created_at).toLocaleString()}</span></div>
                </div>
                {selectedBug.ai_analysis && (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-600">
                      <Bot className="w-3 h-3" /> AI Analysis
                    </div>
                    <div className="text-[10px] text-foreground/80 whitespace-pre-wrap leading-relaxed">{selectedBug.ai_analysis}</div>
                  </div>
                )}
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" className="text-[10px] h-7 flex-1" variant="outline" onClick={() => updateBug(selectedBug.id, { status: 'in_progress' })}>
                    <Clock className="w-3 h-3 mr-1" /> Progress
                  </Button>
                  <Button size="sm" className="text-[10px] h-7 flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={() => updateBug(selectedBug.id, { status: 'resolved' })}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Resolve
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="p-8 text-center text-muted-foreground text-xs italic">Select a bug to view details</Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BugsTab;
